import React, { useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import handleFileExport from '@vendys-frontend/excel-export';
import { useNavigate } from 'react-router-dom';
import { Table, Select, Button, type TableProps } from 'antd';
import { FlexBoxCenter } from 'styles';
import { useGetEvidencesHistories, useGetEvidencesDetail, useGetEvidencesCashReceiptDetail, useMeInfo } from 'hooks';
import { setPassCertification } from 'utils';

import EvidenceCashReceiptModal from 'pages/SalesSettlement/modal/EvidenceCashReceiptModal';
import EvidenceTaxInvoiceModal from 'pages/SalesSettlement/modal/EvidenceTaxInvoiceModal';
import { TEvidenceDetail, TEvidenceCashReceiptDetail } from 'pages/SalesSettlement/schema';
import { WhiteWrapper, EvidenceSearchFormWrapper, TableWrapper } from 'pages/SalesSettlement/styles';

import EvidenceExcelLayout from './EvidenceExcelLayout.json';

const ExcelDownloadButton = styled.div`
  button.excel-download {
    width: auto;
    height: 35px;
    margin: 0;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    background: #4bb378;
  }
`;

const columns: TableProps<any>['columns'] = [
  { title: '증빙 구분', dataIndex: 'transactionTypeName', align: 'center' },
  { title: '품목명', dataIndex: 'itemName', align: 'center' },
  { title: '증빙 유형', dataIndex: 'evidenceTypeName', align: 'center' },
  { title: '공급받는자', dataIndex: 'providerBizName', align: 'center' },
  { title: '발급수단(사업자)번호', dataIndex: 'providerBizNumber', align: 'center' },
  {
    title: '공급가액',
    dataIndex: 'supplyAmount',
    align: 'right',
    render: (value) => (typeof value === 'number' ? value.toLocaleString() : '-')
  },
  {
    title: '부가세',
    dataIndex: 'vatAmount',
    align: 'right',
    render: (value) => (typeof value === 'number' ? value.toLocaleString() : '-')
  },
  {
    title: '총액',
    dataIndex: 'totalAmount',
    align: 'right',
    render: (value) => (typeof value === 'number' ? value.toLocaleString() : '-')
  },
  { title: '작성일자', dataIndex: 'writeDate', align: 'center', render: (value) => value || '-' },
  { title: '승인번호', dataIndex: 'confirmNumber', align: 'center', render: (value) => value || '-' },
  { title: '승인일자', dataIndex: 'sendDate', align: 'center', render: (value) => value || '-' }
];

const diff = moment().diff('2023', 'year') + 1;
const SEARCH_YEAR_OPTIONS = Array.from({ length: diff }).map((_, index) => {
  const year = moment().add(-index, 'year').year();
  return { label: year, value: year };
});

const TRANSACTION_TYPE_OPTIONS = [
  { label: '증빙 구분 전체', value: 'ALL' },
  { label: '매출', value: 'SALES' },
  { label: '매입', value: 'PURCHASE' }
];
const EVIDENCE_TYPE_OPTIONS = [
  { label: '증빙 유형 전체', value: 'ALL' },
  { label: '전자세금계산서', value: 'ELECTRONIC_TAX_INVOICE' },
  { label: '전자계산서', value: 'ELECTRONIC_INVOICE' },
  { label: '종이세금계산서', value: 'PAPER_TAX_INVOICE' },
  { label: '현금영수증', value: 'CASH_RECEIPT' }
];

const EvidenceIssued = () => {
  const navigate = useNavigate();
  const { meInfo } = useMeInfo();

  const [searchParams, setSearchParams] = useState({
    year: moment().year(),
    month: null,
    evidenceType: null,
    transactionType: null
  });
  const [isTaxInvoiceModal, setIsTaxInvoiceModal] = useState<boolean>(false);
  const [isCashReceiptModal, setIsCashReceiptModal] = useState<boolean>(false);

  const [evidenceData, setEvidenceData] = useState<TEvidenceDetail>();
  const [cashReceiptData, setCashReceiptData] = useState<TEvidenceCashReceiptDetail>();

  const { data } = useGetEvidencesHistories({
    ...searchParams,
    evidenceType: searchParams?.evidenceType === 'ALL' ? null : searchParams?.evidenceType,
    transactionType: searchParams?.transactionType === 'ALL' ? null : searchParams?.transactionType
  });
  const { mutateAsync: getEvidencesDetail } = useGetEvidencesDetail(); // 증빙 내역 상세
  const { mutateAsync: getEvidencesCashReceiptDetail } = useGetEvidencesCashReceiptDetail(); // 증빙 내역 상세

  useEffect(() => {
    if (!meInfo?.admin?.isCertification) {
      setPassCertification(meInfo?.admin?.id, false);
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleParamsChange = (name: string, value: string | number) => {
    setSearchParams({ ...searchParams, [name]: value });
  };

  const handleExcelDown = () => {
    const fileName = `부가가치세 신고 참고 자료_${moment().valueOf()}`;
    const searchDate = `${moment(`${searchParams.year}`).startOf('year').format('YYYY-MM-DD')} ~ ${moment(`${searchParams.year}`).endOf('year').format('YYYY-MM-DD')}`;
    const path = window.location.origin;
    const downloadDate = moment().format('YYYY-MM-DD HH:mm:ss');

    const salesArray = data?.content?.filter(({ transactionType }) => transactionType === 'SALES'); // "매출"
    const purchaseArray = data?.content?.filter(({ transactionType }) => transactionType === 'PURCHASE'); // "매입"

    const salesRows = [];
    salesArray.forEach((itme) => {
      salesRows.push([
        itme.transactionTypeName,
        itme.itemName,
        itme.evidenceTypeName,
        itme.providerBizName,
        itme.providerBizNumber,
        typeof itme.supplyAmount === 'number' ? itme.supplyAmount.toLocaleString() : '-',
        typeof itme.vatAmount === 'number' ? itme.vatAmount.toLocaleString() : '-',
        typeof itme.totalAmount === 'number' ? itme.totalAmount.toLocaleString() : '-',
        itme.writeDate,
        itme.confirmNumber,
        itme.sendDate
      ]);
    });
    const purchaseRows = [];
    purchaseArray.forEach((itme) => {
      purchaseRows.push([
        itme.transactionTypeName,
        itme.itemName,
        itme.evidenceTypeName,
        itme.providerBizName,
        itme.providerBizNumber,
        typeof itme.supplyAmount === 'number' ? itme.supplyAmount.toLocaleString() : '-',
        typeof itme.vatAmount === 'number' ? itme.vatAmount.toLocaleString() : '-',
        typeof itme.totalAmount === 'number' ? itme.totalAmount.toLocaleString() : '-',
        itme.writeDate,
        itme.confirmNumber,
        itme.sendDate
      ]);
    });
    const apiExcelDatas = {
      0: {
        startRowNum: 1,
        customValues: [
          { address: 'C3', value: fileName },
          { address: 'C4', value: searchDate },
          { address: 'C5', value: path },
          { address: 'C6', value: downloadDate },
          { address: 'C7', value: '-' }
        ]
      },
      1: { startRowNum: 2, newRows: salesRows },
      2: { startRowNum: 2, newRows: purchaseRows }
    };
    handleFileExport(EvidenceExcelLayout, apiExcelDatas, fileName);
  };

  const handleRowClick = async (values) => {
    if (!values?.evidenceIdx) return;
    if (values?.evidenceType !== 'CASH_RECEIPT') {
      const evidence = await getEvidencesDetail(values?.evidenceIdx);
      setEvidenceData(evidence);
      setIsTaxInvoiceModal(true);
    } else {
      const evidence = await getEvidencesCashReceiptDetail(values?.evidenceIdx);
      setCashReceiptData(evidence);
      setIsCashReceiptModal(true);
    }
  };

  return (
    <div>
      <EvidenceSearchFormWrapper>
        <span style={{ fontSize: '16px', fontWeight: '700' }}>조회기간</span>
        <Select
          defaultValue={moment().year()}
          options={SEARCH_YEAR_OPTIONS}
          onChange={(value) => handleParamsChange('year', value)}
        />
        <Select
          defaultValue="ALL"
          options={TRANSACTION_TYPE_OPTIONS}
          onChange={(value) => handleParamsChange('transactionType', value)}
        />
        <Select
          defaultValue="ALL"
          options={EVIDENCE_TYPE_OPTIONS}
          onChange={(value) => handleParamsChange('evidenceType', value)}
        />
        <div className="evidence-search-guide">
          조회기간은 각 증빙의 <span style={{ fontWeight: 700 }}>작성일자</span>를 기준으로 합니다.
        </div>
      </EvidenceSearchFormWrapper>

      <WhiteWrapper>
        <FlexBoxCenter justify="space-between" style={{ paddingBottom: '8px' }}>
          <span style={{ color: '#6D7582' }}>
            국세청 부가가치세 신고 참고자료로써 증빙내역을 표시하는 페이지입니다. 상세내역은 매출 정산에서 확인해주세요
          </span>
          <ExcelDownloadButton>
            <Button
              className="excel-download vat-excel"
              style={{ borderRadius: '4px', color: '#ffffff' }}
              onClick={handleExcelDown}
            >
              부가가치세 신고 참고 자료 엑셀 다운로드
            </Button>
          </ExcelDownloadButton>
        </FlexBoxCenter>

        <TableWrapper>
          <Table
            columns={columns}
            dataSource={data?.content || []}
            pagination={false}
            onRow={(value) => ({ onClick: () => handleRowClick(value) })}
          />
        </TableWrapper>
        <EvidenceTaxInvoiceModal
          open={isTaxInvoiceModal}
          evidenceData={evidenceData}
          onCancel={() => setIsTaxInvoiceModal(!isTaxInvoiceModal)}
        />
        <EvidenceCashReceiptModal
          open={isCashReceiptModal}
          evidenceData={cashReceiptData}
          onCancel={() => setIsCashReceiptModal(!isCashReceiptModal)}
        />
      </WhiteWrapper>
    </div>
  );
};

export default EvidenceIssued;
